import type {
    CreatureAbilityEntryChargesInput,
    CreatureAbilityEntryInput,
    CreatureAbilityEntryResult,
    CreatureAbilityEntryScalar,
} from '../types';

function phpNumericInteger(value: CreatureAbilityEntryScalar): CreatureAbilityEntryScalar {
    if (typeof value === 'number') return Number.isFinite(value) ? Math.trunc(value) : value;
    if (value === null) return null;
    const trimmed = value.trim();
    if (!/^[+-]?(?:(?:\d+(?:\.\d*)?)|(?:\.\d+))(?:e[+-]?\d+)?$/i.test(trimmed)) return value;
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? Math.trunc(numeric) : value;
}

function phpInt(value: CreatureAbilityEntryScalar): number {
    if (typeof value === 'number') return Number.isFinite(value) ? Math.trunc(value) : 0;
    if (value === null) return 0;
    const match = value.trimStart().match(/^[+-]?(?:(?:\d+(?:\.\d*)?)|(?:\.\d+))(?:e[+-]?\d+)?/i);
    if (!match) return 0;
    const numeric = Number(match[0]);
    return Number.isFinite(numeric) ? Math.trunc(numeric) : 0;
}

function materializeCharges(input: CreatureAbilityEntryChargesInput): CreatureAbilityEntryResult['charges'] {
    const charges: NonNullable<CreatureAbilityEntryResult['charges']> = {
        current: phpNumericInteger(input.current),
        max: phpNumericInteger(input.max),
    };
    if (input.resetsLongRest) charges.resets_long_rest = 1;
    const rechargeBegin = phpInt(input.rechargeRangeBegin);
    const rechargeEnd = phpInt(input.rechargeRangeEnd);
    if (rechargeBegin !== 0 || rechargeEnd !== 0) {
        charges.recharge_begin = rechargeBegin;
        charges.recharge_end = rechargeEnd;
    }
    return charges;
}

/** Materialize build_ability_array's final entry after all external resolution and gates have completed. */
export function materializeCreatureAbilityEntry(input: CreatureAbilityEntryInput): CreatureAbilityEntryResult {
    const source = typeof input.source === 'object' && input.source !== null
        ? input.source.source ?? 'NA'
        : input.source;
    return {
        abilityToken: input.abilityToken,
        source,
        requiresLevel: input.requiresLevel,
        ...(input.consumeNestedUnitPerUse ? { consumeNestedUnitPerUse: true as const } : {}),
        ...(input.charges === null ? {} : { charges: materializeCharges(input.charges) }),
    };
}
